import { useLocation, useNavigate } from 'react-router-dom';
import logoLight from '../assets/navartha-logo-light.png';
import { T } from '../i18n/copy';
import { clinicPath, homePath, privacyPath, switchPath } from '../i18n/paths';

export default function Footer({ lang }) {
  const location = useLocation();
  const navigate = useNavigate();
  const home = homePath(lang);
  const switchLang = (target) =>
    navigate(`${switchPath(location.pathname, target)}${location.hash}`);

  const links = [
    { label: T.nav.howWeWork[lang], href: `${home}#how-we-work` },
    { label: T.nav.whereValue[lang], href: `${home}#where-we-create-value` },
    { label: T.nav.about[lang], href: `${home}#about-navartha` },
    { label: T.nav.clinic[lang], href: clinicPath(lang) },
    { label: { en: 'Privacy', id: 'Privasi' }[lang], href: privacyPath(lang) },
  ];

  return (
    <footer data-header-theme="midnight" data-testid="site-footer" className="bg-midnight text-bone">
      <div className="editorial-margins pt-20 pb-10 md:pt-28">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-12">
          <div className="md:col-span-6">
            <img src={logoLight} alt="Navartha — AI Transformation & Implementation Firm" className="h-auto w-[160px]" />
            <p className="eyebrow mt-4 text-steel-light">{T.footer.descriptor[lang]}</p>
            <a
              href="mailto:hello@navartha.id"
              data-testid="footer-email-link"
              className="mt-10 inline-block max-w-full font-serif text-[20px] text-bone underline decoration-vermilion decoration-1 underline-offset-8 transition-colors hover:text-vermilion md:text-[24px]"
            >
              hello@navartha.id
            </a>
          </div>

          <div className="md:col-span-3">
            <nav className="flex flex-col gap-4" aria-label="Footer">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className="text-[14px] text-bone/65 transition-colors duration-200 hover:text-bone"
                >
                  {l.label}
                </a>
              ))}
            </nav>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow text-bone/40">Language</p>
            <div className="mt-4 flex items-center gap-2" data-testid="footer-language-switcher">
              <button
                type="button"
                data-testid="footer-lang-en"
                onClick={() => lang !== 'en' && switchLang('en')}
                className={`text-[13px] font-medium tracking-[0.14em] ${lang === 'en' ? 'text-bone' : 'text-bone/40 hover:text-bone/75'}`}
              >
                EN
              </button>
              <span className="h-3 w-px bg-bone/25" aria-hidden="true" />
              <button
                type="button"
                data-testid="footer-lang-id"
                onClick={() => lang !== 'id' && switchLang('id')}
                className={`text-[13px] font-medium tracking-[0.14em] ${lang === 'id' ? 'text-bone' : 'text-bone/40 hover:text-bone/75'}`}
              >
                ID
              </button>
            </div>
          </div>
        </div>

        <div className="mt-20 flex flex-col gap-3 border-t border-bone/10 pt-6 md:flex-row md:items-center md:justify-between">
          <p className="text-[12px] text-bone/50" data-testid="footer-copyright">{T.footer.copyright[lang]}</p>
          <p className="font-serif text-[14px] italic text-steel-light" data-testid="footer-tagline">{T.footer.tagline[lang]}</p>
        </div>
      </div>
    </footer>
  );
}
